import React, { useState } from "react";
import Search from "../../../components/form/Search";
import Table from "../../../components/elements/table/Table";
import { handleFilterChange } from "../../../helpers";
import { useDebounce } from "../../../hooks/useDebounce";
import useFetch from "../../../hooks/useFetch";
import {
  resourceDelete,
  resourceist,
  resourceStatusChange,
} from "../../../services/resource";
import Button from "../../../components/form/Button";
import LimitSelect from "../../../components/elements/LimitSelect";
import ManageResourceModal from "./modal/ManageResourceModal";
import { toast } from "react-toastify";
import Switch from "../../../components/form/Switch";
import Badge from "../../../components/elements/Badge";
import useMutation from "../../../hooks/useMutation";
import DeleteModal from "../../../components/common/DeleteModal";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setTitle } from "../../../helpers/MetaTag";
import { useGetPermission } from "../../../hooks/useGetPermission";

function Resources() {
  setTitle("Vpeters Admin | Resource");
  const { permission, status } = useGetPermission("resource");
  const { session } = useSelector((state) => state?.authProvider);
  const isAdmin = session?.user?.roleCode === "admin";
  const [keyword, setKeyword] = useState("");
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const location = useLocation();
  const pathName = location.pathname;

  const search = useDebounce(keyword, 300);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });

  const {
    data: resourceData,
    isLoading: resourceLoading,
    updateData,
    refetch,
  } = useFetch({
    dependencies: ["resourceList", permission?.list, filters, search],
    fetchFunction: async () => {
      const res = await resourceist({
        ...filters,
        ...(pathName.includes("user") && { status: 1 }),
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message || res?.error);
      }
      return res.docs;
    },
    enabled: !!permission?.list && status === "ok",
  });

  const tableHeadData = [
    {
      _id: 2,
      name: "Resource Name",
      align: "left",
    },
    {
      _id: 3,
      name: "Category",
      align: "left",
    },
    {
      _id: 4,
      name: "Description",
      align: "left",
    },
    {
      _id: 5,
      name: "Download Or Visit",
      align: "left",
    },
    ...(isAdmin
      ? [
          {
            _id: 6,
            name: "Status",
            align: "left",
          },
          ...(permission?.edit || permission?.delete
            ? [
                {
                  _id: 7,
                  name: "Action",
                  align: "left",
                },
              ]
            : []),
        ]
      : []),
  ];

  const tableData = [...(resourceData?.data || [])]?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "N/A",
        },
        {
          _id: 21,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.categoryType.charAt(0).toUpperCase() +
              item.categoryType.slice(1) || "N/A",
        },
        {
          _id: 21,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.description || "N/A",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] gap-2",
          data: item?.contentLink ? (
            <a
              href={item?.contentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:underline cursor-pointer"
            >
              <i className="fas fa-link"></i>
              <span>Visit URL</span>
            </a>
          ) : (
            <>
              {item?.content?.url ? (
                <a
                  href={item?.content?.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-600 cursor-pointer hover:underline"
                >
                  <i className="fas fa-download"></i>
                  <span>Download PDF</span>
                </a>
              ) : (
                "N/A"
              )}
            </>
          ),
        },
        ...(isAdmin
          ? [
              {
                _id: "status",
                type: "text",
                align: "left",
                cellClass: "!min-w-[100px]",
                data: (
                  <div className="flex gap-2 items-center">
                    <Switch
                      className={`!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px] ${
                        !permission?.changeStatus ? "opacity-60" : null
                      }`}
                      checked={item?.status === 1}
                      onChange={() => {
                        updateData((pre) => {
                          pre.data = (pre?.data || []).map((elm) =>
                            elm?._id === item?._id
                              ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
                              : elm
                          );
                          return { ...pre };
                        });
                        statusChangeHandler({
                          id: item?._id,
                          status: item?.status === 1 ? 2 : 1,
                        });
                      }}
                      disabled={!permission?.changeStatus}
                    />
                    <Badge
                      type={item?.status === 1 ? "success" : "danger"}
                      label={item?.status === 1 ? "Active" : "Inactive"}
                    />
                  </div>
                ),
              },
              ...(permission?.edit || permission?.delete
                ? [
                    {
                      _id: 6,
                      type: "action",
                      align: "left",
                      cellClass: "w-[5%]",
                      actionData: [
                        ...(permission?.edit && status === "ok"
                          ? [
                              {
                                _id: 1,
                                name: "Edit",
                                icon: "fa-regular fa-pen-to-square",
                                onClick: () => {
                                  setModalOpen({ isOpen: true, data: item });
                                },
                                standout: false,
                              },
                            ]
                          : []),
                        ...(permission?.edit && status === "ok"
                          ? [
                              {
                                _id: 2,
                                name: "Delete",
                                icon: "fa-regular fa-trash-can",
                                standout: true,
                                onClick: () => {
                                  setDeleteModal({
                                    isOpen: true,
                                    data: item,
                                  });
                                },
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
            ]
          : []),
      ],
    };
  });

  //   user status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await resourceStatusChange({
          ids: id,
          status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?._id === payload?.id
                ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
                : elm
            );
            return { ...pre };
          });
          toast.warning(res?.message || res?.error);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  //   Application Form delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await resourceDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (resourceData?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function resourceManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({
            ...pre,
            data: [res?.data, ...(pre?.data || [])],
          }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }

      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: (pre?.data || []).map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }

      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-[#C94E53]  px-6 py-4 rounded-2xl text-center ">
        Resource Library
      </h1>
      {pathName.includes("user") && (
        <h1 className="text-base font-bold   px-3 py-2 rounded-xl  bg-white mb-2">
          Explore tools and tips for funding your education, applying for the
          V’Peters Scholarship, and planning your future—all in the Resource
          Library.
        </h1>
      )}
      <div className="flex gap-2">
        <div className="relative">
          <LimitSelect
            value={filters?.limit}
            allValue={resourceData?.metadata?.totalDocs}
            onChange={(val) => handleFilterChange("limit", val, setFilters)}
          />
        </div>
        <div className="w-72 mb-2">
          <Search
            placeholder={"Search here"}
            search={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
        </div>
        {isAdmin && permission?.add && status === "ok" && (
          <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
              }
              buttonLabel={"Create New"}
              buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div>
        )}
      </div>
      {status === "ok" &&
        (permission?.list ? (
          <div className="mb-2">
            <Table
              tableHeadData={tableHeadData}
              tableData={tableData}
              isLoder={resourceLoading}
              pageCount={resourceData?.metadata?.totalPages}
              currentPage={filters?.page}
              onPageChange={(val) => {
                handleFilterChange("page", val, setFilters);
              }}
              originalData={[...(resourceData?.data || [])]}
              isOrderingDisabled={true}
              tableActionDropdownContainer={"!w-48"}
              // handleOrdering={(newData, oldData) => {
              //   messageOrderingHandler(newData, oldData);
              // }}
              // setOriginalData={(data) =>
              //   updateData((pre) => {
              //     pre.data = data;
              //     return { ...pre };
              //   })
              // }
              // isFetching={isFetching}
              // selectedItems={selectedItems}
              // setSelectedItems={setSelectedItems}
            />
          </div>
        ) : (
          <div className="font-semibold text-xl text-center h-60 flex items-center justify-center text-shades-07">
            You don't have permission to view this list.
          </div>
        ))}
      <ManageResourceModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={resourceManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
}

export default Resources;
